import { Tooltip } from "antd";
import dayjs from "dayjs";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { renderChartEmptyState } from "../../../../../../components/ChartEmptyState";
import { CustomModalChartTick } from "../../../../../../components/ModalChart/CustomModalChartTick/CustomModalChartTick";
import {
  getEmptyAllAxis,
  getGraphSeparators,
} from "../../../../../../utils/chartUtils";
import { formatHoursAndMinutes } from "../../../../../../utils/dateFormatter";
import { DATE_FILTER_ALL, DateFilterOptionType } from "../CustomOptionsBar";
import { CustomFitbitModalChartLegend } from "./CustomFitbitModalChartLegend";
import { TooltipCss } from "./FitbitActivityChart.styles";

export type FitbitChartRow = {
  timeValue: number;
  sedentaryHours: number;
  activeHours: number;
  label?: string;
  isMonthly?: boolean;
  sedentaryPercent?: number;
  lightlyActivePercent?: number;
  fairlyActivePercent?: number;
  veryActivePercent?: number;
  minutesSedentary?: number;
  minutesLightlyActive?: number;
  minutesFairlyActive?: number;
  minutesVeryActive?: number;
};

type FitbitActivityChartProps = {
  data: FitbitChartRow[];
  width: string | number;
  height: string | number;
  isPinnedGraph?: boolean;
  dateInterval: DateFilterOptionType;
  onlyGraph?: boolean;
  isEmpty?: boolean;
  sources?: string[];
};

export function FitbitActivityChart({
  data,
  width,
  height,
  dateInterval,
  isPinnedGraph,
  onlyGraph,
  isEmpty,
  sources,
}: FitbitActivityChartProps) {
  const { t } = useTranslation();

  // Get min/max dates from actual data
  const minDate =
    data.length > 0
      ? dayjs(Math.min(...data.map((d) => d.timeValue)))
      : undefined;
  const maxDate =
    data.length > 0
      ? dayjs(Math.max(...data.map((d) => d.timeValue)))
      : undefined;

  // Check if data is monthly (all bars represent monthly aggregates)
  const isMonthlyData = data.length > 0 && data[0]?.isMonthly === true;

  // For Fitbit, we use data-driven rendering for both monthly and short daily ranges
  // This ensures bars and labels always align
  const dataPointTicks = useMemo(() => {
    if (data.length === 0) return [];
    return data.map((d) => d.timeValue);
  }, [data]);

  const emptyAllAxis = getEmptyAllAxis(dateInterval, isEmpty);

  const graphSeparatorResult = getGraphSeparators(
    dateInterval,
    emptyAllAxis?.anchor ?? (isEmpty ? dateInterval.startDate : minDate),
    emptyAllAxis?.anchor ?? (isEmpty ? dateInterval.endDate : maxDate)
  );

  const {
    graphSeparators,
    dates,
    separatorCounter,
    viewType,
    isTickMultipleLine,
    interval,
  } = graphSeparatorResult;

  // Determine if we should use data points as ticks
  // - Monthly data: always use data points
  // - Daily data with short intervals (48H/24H): use data points for alignment
  // - Otherwise: use standard getGraphSeparators ticks
  const isFitbitDataPointMode =
    isMonthlyData || interval === "48H" || interval === "24H";

  // Determine tick format
  const getTickFormat = (): string => {
    if (isMonthlyData) return "MMM YYYY";
    if (interval === "48H" || interval === "24H") return "MMM D";
    return graphSeparatorResult.tickFormat;
  };
  const tickFormat = getTickFormat();

  // Get x-axis ticks
  const xAxisTicks = useMemo(() => {
    if (isFitbitDataPointMode) return dataPointTicks;
    if (viewType === "yearly") return graphSeparators;
    return dates;
  }, [isFitbitDataPointMode, dataPointTicks, viewType, graphSeparators, dates]);

  // Reference lines (vertical dashed lines)
  const refs = useMemo((): number[] => {
    if (isFitbitDataPointMode) return dataPointTicks;
    return [
      ...graphSeparators,
      ...dates.filter((_, index) => index % separatorCounter === 0),
    ];
  }, [
    isFitbitDataPointMode,
    dataPointTicks,
    graphSeparators,
    dates,
    separatorCounter,
  ]);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState<React.ReactNode>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const [hoveredBarIdx, setHoveredBarIdx] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const chartCardAllView = onlyGraph && dateInterval.id === DATE_FILTER_ALL;

  // Format x-axis tick labels
  const tickXFormatter = (value: string) => {
    if (!isEmpty && (!data || data.length === 0)) return "";

    const numValue = Number(value);

    // For data point mode, show labels only for actual data points
    if (isFitbitDataPointMode) {
      if (dataPointTicks.includes(numValue)) {
        return dayjs(numValue).format(tickFormat);
      }
      return "";
    }

    // For standard mode, show labels for separators and reference ticks
    if (graphSeparators.includes(numValue) || refs.includes(numValue)) {
      if (tickFormat === "Q") {
        return `Q${dayjs(value).format(tickFormat)}, ${dayjs(value).format(
          "YY"
        )}`;
      }
      return dayjs(value).format(tickFormat);
    }

    return "";
  };

  // Calculate x-axis domain
  const handleXDomain = (): [number, number] => {
    if (!dateInterval) return [0, 1];
    if (!isEmpty && (!data || data.length === 0)) return [0, 1];

    if (emptyAllAxis) return emptyAllAxis.domain;

    // For monthly data or ALL view, use actual data range with padding
    if (isMonthlyData || dateInterval.id === DATE_FILTER_ALL) {
      if (minDate && maxDate) {
        const padding = 15 * 24 * 60 * 60 * 1000; // 15 days
        return [minDate.valueOf() - padding, maxDate.valueOf() + padding];
      }
    }

    let { startDate, endDate } = dateInterval;

    // For Fitbit daily data with short intervals, use proper daily padding
    if (interval === "48H" || interval === "24H") {
      const padding = 12 * 60 * 60 * 1000;
      return [
        startDate.startOf("day").valueOf() - padding,
        endDate.endOf("day").valueOf() + padding,
      ];
    }

    if (interval === "7D" || interval === "2M") {
      startDate = startDate.add(-12, "hour");
    }

    if (minDate && maxDate && endDate.diff(startDate, "year") > 5) {
      startDate = minDate;
      endDate = maxDate;
    }

    if (endDate.diff(startDate, "months") > 11) {
      endDate = endDate.add(30, "day");
    }

    const needsPadding =
      interval === "7D" ||
      interval === "1M" ||
      interval === "2M" ||
      interval === "6M" ||
      interval === "1Y" ||
      interval === "4Y" ||
      interval === "4Y+";

    if (needsPadding) {
      const padding = 12 * 60 * 60 * 1000;
      return [startDate.valueOf() - padding, endDate.valueOf() + padding];
    }

    return [startDate.valueOf(), endDate.valueOf()];
  };

  const handleMouseMove = (barData: any, idx: number, e: React.MouseEvent) => {
    const { clientX, clientY } = e;

    setPos((prev) =>
      prev.left === clientX && prev.top === clientY
        ? prev
        : { left: clientX, top: clientY }
    );

    setHoveredBarIdx(idx);

    const payload = barData?.payload;
    const mutedText: React.CSSProperties = { fontSize: 12, color: "#D6D6D6" };

    const sedentaryPercent = payload?.sedentaryPercent ?? 0;
    const lightlyActivePercent = payload?.lightlyActivePercent ?? 0;
    const fairlyActivePercent = payload?.fairlyActivePercent ?? 0;
    const veryActivePercent = payload?.veryActivePercent ?? 0;

    const sedentaryHours = payload?.sedentaryHours ?? 0;
    const lightlyHours = (payload?.minutesLightlyActive ?? 0) / 60;
    const fairlyHours = (payload?.minutesFairlyActive ?? 0) / 60;
    const veryHours = (payload?.minutesVeryActive ?? 0) / 60;
    const activeHours = payload?.activeHours ?? 0;

    const dateLabel = payload?.isMonthly
      ? payload?.label
      : dayjs(payload?.timeValue).format("MMM D, YYYY");

    const title = (
      <div className={TooltipCss()} style={{ alignItems: "flex-start" }}>
        <p style={{ fontWeight: 600, marginBottom: 4 }}>{dateLabel}</p>
        <p style={mutedText}>
          <span style={{ color: "#C87C36" }}>
            {veryActivePercent.toFixed(1)}%
          </span>{" "}
          {t("patientPage_measurementModal_activity_veryActive", {
            duration: formatHoursAndMinutes(veryHours),
          })}
        </p>
        <p style={mutedText}>
          <span style={{ color: "#D4976A" }}>
            {fairlyActivePercent.toFixed(1)}%
          </span>{" "}
          {t("patientPage_measurementModal_activity_fairlyActive", {
            duration: formatHoursAndMinutes(fairlyHours),
          })}
        </p>
        <p style={mutedText}>
          <span style={{ color: "#E5B89E" }}>
            {lightlyActivePercent.toFixed(1)}%
          </span>{" "}
          {t("patientPage_measurementModal_activity_lightlyActive", {
            duration: formatHoursAndMinutes(lightlyHours),
          })}
        </p>
        <p style={mutedText}>
          <span style={{ color: "#54A6AA" }}>
            {sedentaryPercent.toFixed(1)}%
          </span>{" "}
          {t("patientPage_measurementModal_activity_sedentary", {
            duration: formatHoursAndMinutes(sedentaryHours),
          })}
        </p>
        <p style={{ ...mutedText, marginTop: 4, fontWeight: 500 }}>
          {t("patientPage_measurementModal_activity_totalActive", {
            duration: formatHoursAndMinutes(activeHours),
          })}
        </p>
      </div>
    );

    setTooltipTitle(title);
    setTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
    setHoveredBarIdx(null);
  };

  // Calculate max Y domain based on data
  const maxYDomain = useMemo(() => {
    if (!data || data.length === 0) return 24;
    const maxTotal = Math.max(
      ...data.map((d) => d.sedentaryHours + d.activeHours)
    );
    return Math.min(24, Math.ceil(maxTotal + 1));
  }, [data]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        paddingRight: "12px",
        minWidth: isEmpty ? 600 : undefined,
      }}
    >
      <Tooltip
        open={tooltipOpen}
        title={tooltipTitle}
        placement="top"
        zIndex={3000}
        arrow={false}
        styles={{
          body: {
            padding: "10px 20px",
            minHeight: "40px",
            width: "max-content",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Open Sans', sans-serif",
            fontSize: "12px !important",
            fontWeight: "500",
            backgroundColor: "#4b4b4b",
          },
        }}
        mouseEnterDelay={0.1}
      >
        <span
          style={{
            position: "fixed",
            left: pos.left,
            top: pos.top,
            width: 1,
            height: 1,
          }}
        />
      </Tooltip>

      <ResponsiveContainer
        debounce={10}
        width={width}
        height={height}
        minWidth={isPinnedGraph ? 658 : undefined}
      >
        <BarChart
          style={{
            width: "100%",
            maxHeight: "70vh",
            cursor: "pointer",
          }}
          data={data}
          margin={
            onlyGraph
              ? {
                  top: 5,
                  right: 5,
                  left: 5,
                  bottom: 5,
                }
              : {
                  top: 5,
                  right: 32,
                  left: 5,
                  bottom: 5,
                }
          }
        >
          {!onlyGraph ? (
            <CartesianGrid strokeDasharray="2" vertical={false} />
          ) : null}

          <XAxis
            tickMargin={14}
            stroke="#D8D8D8"
            type={chartCardAllView ? "category" : "number"}
            scale="time"
            ticks={xAxisTicks}
            domain={
              chartCardAllView
                ? [
                    (minDate?.valueOf() ?? 0) - 15 * 24 * 60 * 60 * 1000,
                    (maxDate?.valueOf() ?? 0) + 15 * 24 * 60 * 60 * 1000,
                  ]
                : handleXDomain()
            }
            tickFormatter={tickXFormatter}
            allowDuplicatedCategory={false}
            tickLine={false}
            height={refs.length >= 6 ? 70 : 48}
            minTickGap={refs.length >= 6 ? 20 : 100}
            interval={0}
            hide={onlyGraph}
            dataKey="timeValue"
            tick={
              isTickMultipleLine && !isFitbitDataPointMode ? (
                <CustomModalChartTick isEmpty={isEmpty} />
              ) : (
                {
                  fill: isEmpty ? "#B5B5B6" : "#727272",
                  fontSize: "12px",
                  fontWeight: "400",
                }
              )
            }
            angle={refs.length >= 6 ? -45 : 0}
          />

          <YAxis
            label={
              !onlyGraph
                ? {
                    value: t("patientPage_measurementModal_hoursAxis"),
                    angle: -90,
                    position: "insideLeft",
                    dx: -5,
                    dy: 20,
                    style: {
                      fontSize: "12px",
                      color: "#727272CC",
                      marginLeft: 12,
                    },
                  }
                : undefined
            }
            tickMargin={8}
            stroke="#D8D8D8"
            domain={[0, maxYDomain]}
            tickCount={Math.min(maxYDomain + 1, 13)}
            width={48}
            hide={onlyGraph}
            tick={{
              fill: isEmpty ? "#B5B5B6" : "#727272",
              fontSize: "12px",
              fontWeight: 400,
            }}
            allowDuplicatedCategory={false}
            allowDecimals={false}
          />

          {!onlyGraph
            ? refs.map((ref) => (
                <ReferenceLine
                  key={ref.toString()}
                  x={ref}
                  stroke="#D8D8D8"
                  strokeDasharray="8 1000"
                />
              ))
            : null}

          {!onlyGraph ? (
            <Legend
              content={
                <CustomFitbitModalChartLegend
                  data={data}
                  sources={sources}
                  startDate={
                    dateInterval.id === DATE_FILTER_ALL && minDate
                      ? minDate
                      : dateInterval.startDate
                  }
                  endDate={dateInterval.endDate}
                />
              }
            />
          ) : null}

          <Bar
            dataKey="sedentaryHours"
            stackId="activity"
            fill="#54A6AA"
            radius={[0, 0, 0, 0]}
            isAnimationActive={false}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            barSize={onlyGraph ? 6 : 18}
          >
            {data.map((row, idx) => (
              <Cell
                key={`sed-${row.timeValue}`}
                opacity={hoveredBarIdx === idx ? 0.8 : 1}
              />
            ))}
          </Bar>

          <Bar
            dataKey="activeHours"
            stackId="activity"
            fill="#C87C36"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            barSize={onlyGraph ? 6 : 18}
          >
            {data.map((row, idx) => (
              <Cell
                key={`act-${row.timeValue}`}
                opacity={hoveredBarIdx === idx ? 0.8 : 1}
              />
            ))}
          </Bar>

          {isEmpty &&
            renderChartEmptyState({
              message: t("chart_noDataForRange"),
            })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
